import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService.js';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br.js';

dayjs.locale('pt-br');

function DashboardHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [hovered, setHovered] = useState({});

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const result = await adminService.getDashboard();
      setData(result);
    } catch (err) {
      toast.error('Erro ao carregar dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="stats-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="shimmer shimmer-card"></div>
          ))}
        </div>
        <div className="charts-grid">
          <div className="shimmer shimmer-chart-large"></div>
          <div className="shimmer shimmer-chart-small"></div>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const charts = data?.charts || {};

  const entityTypeLabels = {
    company: 'Clínica ou Hospital Veterinário',
    personal: 'Profissional Autônomo (Veterinário)',
    others: 'Outros',
  };

  const formatEntityType = (type) => entityTypeLabels[type] || type || '-';

  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '-';
    const d = dayjs(monthStr);
    if (!d.isValid()) return monthStr;
    const s = d.format('MMM YYYY');
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // Helper: formata um número como porcentagem (sem sinal) com até 1 casa decimal
  const formatPercentage = (v) => {
    if (v == null || isNaN(v)) return '0%';
    const n = Number(v);
    const abs = Math.abs(n);
    const fixed = (abs % 1 === 0) ? abs.toFixed(0) : abs.toFixed(1);
    return `${fixed}%`;
  };

  const statCards = [
    {
      label: 'Usuários Ativos',
      value: metrics.totalUsers?.toLocaleString('pt-BR') || '0',
      change: `${metrics.newUsersLastMonth || 0} últimos 28 dias`,
      positive: true,
      color: '#7165FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      label: 'Negócios',
      value: metrics.totalEntitiesCreated?.toLocaleString('pt-BR') || '0',
      change: `${metrics.totalEntitiesActive || 0} ativos`,
      positive: true,
      color: '#7165FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="7" width="18" height="13" rx="2" ry="2" />
          <path d="M8 7V5a4 4 0 0 1 8 0v2" />
        </svg>
      ),
    },
    {
      label: 'Pets Cadastrados',
      value: metrics.totalPets?.toLocaleString('pt-BR') || '0',
      change: 'Total na plataforma',
      positive: true,
      color: '#7165FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
          <g>
            <path fill="currentColor" d="M120.113,139.014c4.529,13.248,13.07,24.156,23.738,31.637c10.672,7.491,23.51,11.665,36.848,11.673
    c6.896,0,13.906-1.126,20.726-3.458l0.023-0.008c13.24-4.528,24.144-13.066,31.626-23.738c7.49-10.664,11.668-23.502,11.672-36.844
    c0-6.891-1.126-13.917-3.469-20.753c-4.525-13.232-13.063-24.14-23.726-31.63c-10.668-7.49-23.502-11.672-36.848-11.672
    c-6.888,0-13.913,1.119-20.765,3.474c-13.236,4.528-24.14,13.066-31.614,23.731c-7.494,10.664-11.672,23.501-11.676,36.843
    c0,6.876,1.122,13.878,3.45,20.698L120.113,139.014z M154.716,99.964c3.749-5.308,9.042-9.475,15.689-11.751l-1.39,0.473
    l1.41-0.489c3.43-1.174,6.868-1.717,10.278-1.717c6.565-0.008,12.984,2.072,18.296,5.813c5.312,3.749,9.475,9.042,11.759,15.682
    v0.007c1.182,3.442,1.728,6.892,1.728,10.294c0.004,6.569-2.075,12.988-5.808,18.304c-3.749,5.317-9.042,9.475-15.689,11.759h0.023
    c-3.458,1.174-6.915,1.717-10.314,1.717c-6.572,0.008-12.991-2.071-18.304-5.804c-5.3-3.733-9.456-9.01-11.743-15.641l-0.016-0.04
    c-1.182-3.458-1.729-6.9-1.729-10.302C148.904,111.7,150.983,105.281,154.716,99.964z"/>
            <path fill="currentColor" d="M120.109,139.014c-0.003-0.016-0.003-0.032-0.012-0.048l-0.422-1.228L120.109,139.014z" />
            <path fill="currentColor" d="M312.969,175.244l-2.182-0.748l2.198,0.756c6.833,2.339,13.846,3.458,20.73,3.458
    c13.346-0.008,26.18-4.182,36.852-11.672c10.668-7.482,19.21-18.391,23.738-31.63c2.343-6.844,3.466-13.862,3.466-20.753
    c-0.004-13.342-4.182-26.18-11.676-36.851c-7.478-10.664-18.39-19.21-31.634-23.731l0.007,0.007
    c-6.828-2.347-13.849-3.481-20.753-3.481c-13.342,0.008-26.18,4.19-36.848,11.68c-10.664,7.482-19.205,18.39-23.726,31.638
    c-2.344,6.829-3.47,13.854-3.47,20.746c0.004,13.342,4.182,26.18,11.672,36.843C288.825,162.169,299.729,170.715,312.969,175.244z
     M303.659,104.367c2.284-6.655,6.446-11.948,11.759-15.697c5.312-3.734,11.727-5.813,18.296-5.813c3.39,0,6.84,0.552,10.294,1.732
    l0.008,0.008c6.648,2.276,11.936,6.435,15.686,11.751c3.733,5.316,5.812,11.735,5.808,18.304c0,3.403-0.548,6.852-1.725,10.302
    c-2.284,6.64-6.45,11.932-11.763,15.682c-5.312,3.734-11.736,5.812-18.308,5.812c-3.403,0-6.856-0.552-10.302-1.725l2.182,0.748
    l-2.166-0.74c-6.647-2.292-11.94-6.458-15.689-11.767c-3.733-5.317-5.812-11.736-5.808-18.304
    C301.931,111.259,302.478,107.809,303.659,104.367z"/>
            <path fill="currentColor" d="M500.324,202.392c-7.478-10.664-18.391-19.209-31.634-23.73h0.008c-6.829-2.339-13.85-3.474-20.753-3.466
    c-13.342,0-26.18,4.174-36.848,11.672c-10.664,7.482-19.205,18.398-23.726,31.638c-2.343,6.829-3.469,13.854-3.469,20.754
    c0.003,13.334,4.182,26.172,11.672,36.836c7.483,10.672,18.387,19.209,31.626,23.738l0.016,0.008
    c6.832,2.332,13.846,3.458,20.73,3.458c13.346-0.008,26.18-4.182,36.852-11.673c10.668-7.482,19.209-18.39,23.738-31.638
    c2.343-6.836,3.466-13.854,3.466-20.746C511.996,225.902,507.818,213.064,500.324,202.392z M478.015,249.546
    c-2.284,6.639-6.45,11.932-11.763,15.681c-5.312,3.733-11.736,5.812-18.308,5.804c-3.403,0-6.856-0.543-10.302-1.717h0.016
    c-6.647-2.284-11.94-6.45-15.689-11.766c-3.733-5.308-5.812-11.727-5.808-18.288c0-3.403,0.547-6.852,1.728-10.302
    c2.284-6.655,6.446-11.948,11.759-15.697c5.312-3.732,11.727-5.812,18.296-5.804c3.39,0,6.84,0.544,10.293,1.725l0.008,0.008
    c6.647,2.276,11.936,6.442,15.685,11.751c3.733,5.308,5.812,11.736,5.809,18.304C479.74,242.646,479.192,246.096,478.015,249.546z"
            />
            <path fill="currentColor" d="M124.634,259.989c2.343-6.836,3.466-13.854,3.466-20.746c-0.004-13.342-4.183-26.18-11.677-36.852
    c-7.478-10.664-18.39-19.209-31.634-23.73l-0.024-0.008c-6.816-2.331-13.83-3.466-20.722-3.458
    c-13.342,0-26.18,4.174-36.848,11.672C16.532,194.35,7.99,205.266,3.469,218.506v-0.007C1.126,225.335,0,232.36,0,239.26
    c0.004,13.334,4.182,26.172,11.672,36.836c7.483,10.672,18.387,19.209,31.626,23.738l0.016,0.008
    c6.832,2.332,13.846,3.458,20.73,3.458c13.346-0.008,26.18-4.182,36.852-11.673C111.564,284.145,120.106,273.237,124.634,259.989z
     M94.114,249.546c-2.284,6.639-6.451,11.932-11.763,15.681c-5.312,3.733-11.735,5.812-18.308,5.804
    c-3.402,0-6.856-0.543-10.302-1.717h0.016c-6.647-2.284-11.94-6.45-15.689-11.766c-3.734-5.308-5.813-11.727-5.808-18.288
    c0-3.411,0.547-6.852,1.728-10.302l0.012-0.032c2.288-6.639,6.442-11.924,11.746-15.666c5.313-3.732,11.728-5.812,18.296-5.804
    c3.391,0,6.84,0.544,10.294,1.725l3.052,1.048l-3.044-1.04c6.647,2.276,11.936,6.442,15.686,11.751
    c3.732,5.308,5.812,11.736,5.808,18.304C95.839,242.646,95.292,246.096,94.114,249.546z"/>
            <path fill="currentColor" d="M338.475,234.219c-12.318-9.83-25.66-17.973-39.687-23.754c-14.02-5.766-28.774-9.168-43.739-9.168
    c-19.966,0.016-39.502,6.017-57.526,15.712c-27.039,14.594-51.025,37.529-68.734,64.182c-17.626,26.66-29.134,57.18-29.204,87.755
    c0,11.105,1.568,22.194,5.04,32.898c3.666,11.318,8.585,20.981,14.674,29c9.097,12.066,20.891,20.115,33.122,24.636
    c12.259,4.56,24.829,5.915,36.864,5.922c13.24-0.007,25.975-1.654,37.364-3.174c11.349-1.544,21.458-2.915,28.401-2.882
    c6.938-0.032,17.051,1.339,28.397,2.882c11.393,1.52,24.128,3.166,37.368,3.174c15.988-0.015,33.17-2.402,48.867-11.633
    c7.801-4.599,15.047-10.908,21.119-18.925c6.088-8.018,11.007-17.682,14.674-28.992c3.473-10.711,5.04-21.8,5.04-32.905
    c-0.016-20.376-5.21-40.751-13.814-59.952C383.734,280.207,363.084,253.893,338.475,234.219z M374.788,391.897
    c-2.733,8.427-6.08,14.689-9.688,19.446c-5.446,7.096-11.42,11.137-18.718,13.917c-7.274,2.732-15.996,3.891-25.569,3.883
    c-10.51,0.008-21.899-1.378-33.076-2.882c-11.215-1.489-22.112-3.142-32.689-3.174c-10.577,0.031-21.474,1.685-32.689,3.165
    c-11.176,1.512-22.565,2.899-33.076,2.891c-12.818,0.016-23.896-2.056-32.457-7.152c-4.328-2.575-8.207-5.898-11.83-10.648
    c-3.607-4.757-6.954-11.018-9.688-19.446c-2.332-7.199-3.466-14.87-3.466-22.951c-0.015-14.791,3.879-30.929,10.987-46.737
    c10.605-23.714,28.436-46.484,48.906-62.772c10.223-8.159,21.072-14.712,31.846-19.138c10.782-4.442,21.438-6.75,31.465-6.742
    c13.354-0.016,27.893,4.127,42.239,11.861c21.517,11.53,42.258,31.094,57.136,53.596c14.96,22.478,23.899,47.76,23.829,69.931
    C378.254,377.027,377.119,384.698,374.788,391.897z"/>
          </g>
        </svg>
      ),
    },
    // {
    //   label: 'Agendamentos',
    //   value: metrics.totalSchedules?.toLocaleString('pt-BR') || '0',
    //   change: 'Total realizados',
    //   positive: false,
    //   color: '#D97706',
    //   icon: (
    //     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    //       <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    //     </svg>
    //   ),
    // },
    // Taxa de crescimento será exibida como porcentagem com seta e cor
    {
      label: 'Taxa Crescimento',
      value: metrics.growthRate != null ? `${metrics.growthRate >= 0 ? '+' : '-'}${formatPercentage(metrics.growthRate)}` : '0%',
      change: 'vs mês anterior',
      changeValue: metrics.growthRate != null ? Number(metrics.growthRate) : undefined,
      positive: metrics.growthRate != null ? Number(metrics.growthRate) >= 0 : true,
      changeColor: (metrics.growthRate != null && Number(metrics.growthRate) >= 0) ? '#10B981' : '#EF4444',
      color: '#7165FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
        </svg>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* Stats Cards */}
      <div className="stats-grid">
        {statCards.map((card, index) => (
          <div key={index} className="stat-card">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <span className="stat-label">{card.label}</span>
                <span className="stat-value">{card.value}</span>
                <span
                  className={`stat-change ${card.positive ? 'positive' : 'negative'}`}
                  style={card.changeColor ? { color: card.changeColor } : undefined}
                >
                  {card.changeValue != null ? (card.changeValue >= 0 ? '↑' : '↓') : (card.positive ? '↑' : '↓')}
                  {' '}
                  {card.changeValue != null ? `${formatPercentage(card.changeValue)}` : card.change}
                  {card.changeValue != null ? ` ${card.change}` : null}
                </span>
              </div>
              <div className="stat-icon" style={{ background: `${card.color}15` }}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Novos Usuários</h3>
          </div>
          <div className="chart-body">
            {charts.registrationsByMonth && charts.registrationsByMonth.length > 0 ? (
              (() => {
                const items = charts.registrationsByMonth;
                const values = items.map(it => it.users ?? it.count ?? 0);
                const max = Math.max(...values, 1);
                const width = 480;
                const height = 200;
                const pad = 30;
                const stepX = items.length > 1 ? (width - pad * 2) / (items.length - 1) : 0;

                const points = items.map((it, idx) => {
                  const v = it.users ?? it.count ?? 0;
                  const x = pad + (idx * stepX);
                  const y = pad + (height - pad * 2) * (1 - (v / max));
                  return `${x},${y}`;
                });

                const polyPoints = points.join(' ');
                const areaPath = `M ${points.join(' L ')} L ${width - pad},${height - pad} L ${pad},${height - pad} Z`;

                return (
                  <div className="simple-chart line-chart" style={{ padding: '8px' }}>
                    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet"
                      onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relX = e.clientX - rect.left;
                        const svgX = (relX / rect.width) * width;
                        let idx = 0;
                        if (stepX > 0) idx = Math.round((svgX - pad) / stepX);
                        idx = Math.max(0, Math.min(items.length - 1, idx));
                        const it = items[idx];
                        const v = it.users ?? it.count ?? 0;
                        setHovered(h => ({ ...h, registrations: idx }));
                        setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${v}` });
                      }}
                      onMouseLeave={() => { setHovered(h => ({ ...h, registrations: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }}
                    >
                      <path d={areaPath} fill="rgba(79,70,229,0.06)" />
                      <polyline points={polyPoints} fill="none" stroke="var(--chart-primary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

                      {items.map((it, i) => {
                        const v = it.users ?? it.count ?? 0;
                        const x = pad + (i * stepX);
                        const y = pad + (height - pad * 2) * (1 - (v / max));
                        const dim = (hovered.registrations == null) || hovered.registrations === i ? 1 : 0.28;
                        return (
                          <g key={i}>
                            <circle cx={x} cy={y} r={hovered.registrations === i ? 5 : 3} fill="var(--chart-primary)" opacity={dim}
                              onMouseEnter={(e) => { setHovered(h => ({ ...h, registrations: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${v}` }); }}
                              onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                              onMouseLeave={() => { setHovered(h => ({ ...h, registrations: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }}
                            />
                            <text x={x} y={height - 6} fontSize="10" fill="var(--text-muted)" textAnchor="middle">{formatMonthLabel(it.month)}</text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                );
              })()
            ) : (
              <div className="empty-chart">Sem dados disponíveis</div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Novos Negócios</h3>
          </div>
          <div className="chart-body">
            {(() => {
              const fallback = [
                { month: '2025-11', entities: 1 },
                { month: '2025-12', entities: 7 },
                { month: '2026-01', entities: 26 },
                { month: '2026-02', entities: 1 },
              ];
              const items = charts.entitiesByMonth && charts.entitiesByMonth.length > 0 ? charts.entitiesByMonth : fallback;
              if (!items || items.length === 0) return <div className="empty-chart">Sem dados disponíveis</div>;

              const values = items.map(it => it.entities ?? it.count ?? 0);
              const max = Math.max(...values, 1);
              const width = 480;
              const height = 200;
              const pad = 30;
              const stepX = items.length > 1 ? (width - pad * 2) / (items.length - 1) : 0;

              const points = items.map((it, idx) => {
                const v = it.entities ?? it.count ?? 0;
                const x = pad + (idx * stepX);
                const y = pad + (height - pad * 2) * (1 - (v / max));
                return `${x},${y}`;
              });

              const polyPoints = points.join(' ');
              const areaPath = `M ${points.join(' L ')} L ${width - pad},${height - pad} L ${pad},${height - pad} Z`;

              return (
                <div className="simple-chart line-chart" style={{ padding: '8px' }}>
                  <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet"
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relX = e.clientX - rect.left;
                      const svgX = (relX / rect.width) * width;
                      let idx = 0;
                      if (stepX > 0) idx = Math.round((svgX - pad) / stepX);
                      idx = Math.max(0, Math.min(items.length - 1, idx));
                      const it = items[idx];
                      const v = it.entities ?? it.count ?? 0;
                      setHovered(h => ({ ...h, entitiesByMonth: idx }));
                      setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${v} negócios` });
                    }}
                    onMouseLeave={() => { setHovered(h => ({ ...h, entitiesByMonth: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }}
                  >
                    <path d={areaPath} fill="rgba(79,70,229,0.06)" />
                    <polyline points={polyPoints} fill="none" stroke="var(--chart-primary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

                    {items.map((it, i) => {
                      const v = it.entities ?? it.count ?? 0;
                      const x = pad + (i * stepX);
                      const y = pad + (height - pad * 2) * (1 - (v / max));
                      const dim = (hovered.entitiesByMonth == null) || hovered.entitiesByMonth === i ? 1 : 0.28;
                      return (
                        <g key={i}>
                          <circle cx={x} cy={y} r={hovered.entitiesByMonth === i ? 5 : 3} fill="var(--chart-primary)" opacity={dim}
                            onMouseEnter={(e) => { setHovered(h => ({ ...h, entitiesByMonth: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${v} negócios` }); }}
                            onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                            onMouseLeave={() => { setHovered(h => ({ ...h, entitiesByMonth: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }}
                          />
                          <text x={x} y={height - 6} fontSize="10" fill="var(--text-muted)" textAnchor="middle">{formatMonthLabel(it.month)}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              );
            })()}
          </div>
        </div>

        
      </div>

      {/* Charts row 2 */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Distribuição por Tipo de Negócio</h3>
          </div>
          <div className="chart-body">
            {charts.entitiesByType && charts.entitiesByType.length > 0 ? (
              (() => {
                const colors = ['var(--chart-primary)', 'var(--chart-green)', 'var(--chart-amber)', 'var(--chart-red)', 'var(--chart-violet)', 'var(--chart-cyan)'];
                const items = charts.entitiesByType;
                const total = items.reduce((s, t) => s + (t.count || 0), 0) || 0;

                const polarToCartesian = (cx, cy, r, angleInDegrees) => {
                  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
                  return {
                    x: cx + (r * Math.cos(angleInRadians)),
                    y: cy + (r * Math.sin(angleInRadians))
                  };
                };

                const describeArc = (cx, cy, r, startAngle, endAngle) => {
                  const start = polarToCartesian(cx, cy, r, endAngle);
                  const end = polarToCartesian(cx, cy, r, startAngle);
                  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
                  return [`M ${cx} ${cy}`, `L ${start.x} ${start.y}`, `A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`, 'Z'].join(' ');
                };

                let acc = 0;
                const cx = 90, cy = 90, r = 70;

                const slices = items.map((item, i) => {
                  const value = item.count || 0;
                  const angle = total > 0 ? (value / total) * 360 : 0;
                  const startAngle = acc;
                  const endAngle = acc + angle;
                  const path = describeArc(cx, cy, r, startAngle, endAngle);
                  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                  const slice = { path, startAngle, endAngle, pct, value, item, color: colors[i % colors.length], index: i };
                  acc = endAngle;
                  return slice;
                });

                return (
                  <div className="pie-chart-wrap" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <svg width="180" height="180" viewBox="0 0 180 180" style={{ flex: '0 0 180px' }}>
                      {slices.map((s) => (
                        <path key={s.index}
                          d={s.path}
                          fill={s.color}
                          opacity={(hovered.entitiesByType == null) || hovered.entitiesByType === s.index ? 1 : 0.28}
                          onMouseEnter={(e) => { setHovered(h => ({ ...h, entitiesByType: s.index })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${s.value} (${s.pct}%)` }); }}
                          onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))}
                          onMouseLeave={() => { setHovered(h => ({ ...h, entitiesByType: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }}
                        />
                      ))}
                      <circle cx={cx} cy={cy} r={r - 28} fill="#fff" />
                    </svg>

                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {items.map((item, i) => {
                          const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0';
                          return (
                            <div key={i} className="distribution-item" onMouseEnter={(e) => { setHovered(h => ({ ...h, entitiesByType: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${item.count} (${pct}%)` }); }} onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))} onMouseLeave={() => { setHovered(h => ({ ...h, entitiesByType: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: (hovered.entitiesByType == null) || hovered.entitiesByType === i ? 1 : 0.28 }}>
                              <div className="distribution-color" style={{ background: colors[i % colors.length] }}></div>
                              <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                                <span className="distribution-name">{formatEntityType(item.type)}</span>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                  <span className="distribution-pct">{pct}%</span>
                                  <span className="distribution-count">{item.count}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="empty-chart">Sem dados disponíveis</div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Serviços Mais Ofertados</h3>
          </div>
          <div className="chart-body">
            {charts.topServices && charts.topServices.length > 0 ? (
              <div className="simple-chart horizontal">
                {charts.topServices.slice(0, 10).map((item, i) => (
                  <div key={i} className="chart-bar-group horizontal" onMouseEnter={(e) => { setHovered(h => ({ ...h, topServices: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${item.count}` }); }} onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))} onMouseLeave={() => { setHovered(h => ({ ...h, topServices: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }} style={{ opacity: (hovered.topServices == null) || hovered.topServices === i ? 1 : 0.28 }}>
                    <span className="chart-bar-label-h">{item.service?.name || item.name}</span>
                    <div className="chart-bar-wrapper">
                      <div className="chart-bar horizontal" style={{ width: `${Math.max((item.count / Math.max(...charts.topServices.map(s => s.count), 1)) * 100, 2)}%`, background: 'var(--chart-primary)' }}></div>
                    </div>
                    <span className="chart-bar-value">{item.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-chart">Sem dados disponíveis</div>
            )}
          </div>
        </div>
        {/* <div className="chart-card">
          <div className="chart-header">
            <h3>Agendamentos por Mês</h3>
          </div>
          <div className="chart-body">
            {charts.schedulesByMonth && charts.schedulesByMonth.length > 0 ? (
              <div className="simple-chart">
                  {charts.schedulesByMonth.map((item, i) => (
                    <div key={i} className="chart-bar-group" onMouseEnter={(e) => { setHovered(h => ({ ...h, schedulesByMonth: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${item.count}` }); }} onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))} onMouseLeave={() => { setHovered(h => ({ ...h, schedulesByMonth: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }} style={{ opacity: (hovered.schedulesByMonth == null) || hovered.schedulesByMonth === i ? 1 : 0.28 }}>
                      <div className="chart-bar" style={{ height: `${Math.max((item.count / Math.max(...charts.schedulesByMonth.map(s => s.count), 1)) * 200, 4)}px`, background: 'var(--chart-violet)' }}></div>
                      <span className="chart-bar-label">{formatMonthLabel(item.month)}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="empty-chart">Sem dados disponíveis</div>
            )}
          </div>
        </div> */}
      </div>
      {tooltip.visible && (
        <div className="cursor-tooltip" style={{ left: tooltip.x + 'px', top: tooltip.y + 'px' }}>{tooltip.content}</div>
      )}
    </div>
  );
}

export default DashboardHome;
